/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Stack,
    Text,
    Container,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useOptimizelyPage} from '../../../hooks/use-optimizely-page'
import {useCartCMSContent} from './use-cart-cms-content'
import {renderCarouselItems, renderLayoutsSwitch} from '../../../components/optimizely/utils/contentRenderer'
import {useMemo} from 'react'
import {useVickyAIRecommendations} from '../../../hooks/use-vicky-pdp-recommendations'
import {findVickyCarouselBlocks, injectVickyDataIntoContent} from '../../../utils/vicky-cms-utils'
import Seo from '../../../components/seo'
import useCanonicalUrl from '../../../hooks/use-canonical-url'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSeoBrandName} from '../../../utils/seo'

/* Contexts */
import { useGetLinkData } from '../../../contexts/category-url-context'

const EmptyCart = ({isMiniCart}) => {
    // PWA Kit <Link> already applies buildUrl — skip hook-level localization
    const getLinkData = useGetLinkData({localize: false})
    const {data: optimizelyData, isLoading: isOptimizelyLoading} = useOptimizelyPage('empty-cart')

    // Fetch VickyAI recommendations for empty cart
    const {products: vickyAIData, isLoading: isVickyLoading} = useVickyAIRecommendations('recommendations', null, 'EmptyCart')

    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const canonicalUrl = useCanonicalUrl()
    const seoBrandName = getSeoBrandName(Boolean(locale?.isSupportedByESW))
    const cartPageTitle = formatMessage(
        {
            id: 'cart.page.title',
            defaultMessage: 'My Shopping Bag | {seoBrandName}'
        },
        {seoBrandName}
    )
    const cartPageDescription = formatMessage(
        {
            id: 'cart.page.description',
            defaultMessage: 'My Shopping Bag | {seoBrandName}'
        },
        {seoBrandName}
    )

    // Inject VickyAI data into CMS content
    const processedOptimizelyData = useMemo(() => {
        if (!optimizelyData) return null

        const modifiedOptimizelyData = JSON.parse(JSON.stringify(optimizelyData))
        const vickyBlocks = findVickyCarouselBlocks(modifiedOptimizelyData)

        if (vickyBlocks.length > 0 && vickyAIData) {
            return injectVickyDataIntoContent(modifiedOptimizelyData, vickyAIData)
        }

        return modifiedOptimizelyData
    }, [optimizelyData, vickyAIData])

    const {banners, carousels, emptyMessage} = useCartCMSContent(processedOptimizelyData)

    const linkData = useMemo(() => {
        return getLinkData(emptyMessage?.CTA?.Link?.Expanded)
    }, [emptyMessage?.CTA?.Link?.Expanded])

    return (
        <Box data-testid="sf-cart-empty" flex="1">
            <Seo
                title={cartPageTitle}
                description={cartPageDescription}
                canonicalUrl={!isMiniCart ? canonicalUrl : undefined}
                noIndex
                noFollow
            >
                {!isMiniCart && <link rel="canonical" href={canonicalUrl} />}
            </Seo>
            <Container
                maxWidth="container.xl"
                px={isMiniCart ? 0 : [4, 6, 6, 4]}
                paddingTop={{base: 8, lg: isMiniCart ? 0 : 8}}
                paddingBottom={{base: 8, lg: 14}}
            >
                {/* Category Banners - Top Area */}
                {!isMiniCart && (isOptimizelyLoading || banners.length > 0) && (
                    <Stack spacing={4} mb={8} w="100%" minHeight={isOptimizelyLoading ? '200px' : undefined}>
                        <Skeleton isLoaded={!isOptimizelyLoading} height={isOptimizelyLoading ? '200px' : 'auto'}>
                            {banners.map((banner, index) => (
                                <React.Fragment key={`cart-banner-${index}`}>
                                    {renderLayoutsSwitch(banner)}
                                </React.Fragment>
                            ))}
                        </Skeleton>
                    </Stack>
                )}

                {/* Empty Cart Message */}
                <Stack marginTop={isMiniCart ? 20 : '100px'} marginBottom={isMiniCart ? 24 : '100px'}>
                    <Box textAlign="center">
                        <Text
                            lineHeight={1}
                            align={'center'}
                            fontSize={['18px', 'xl']}
                            fontWeight="bold"
                            textTransform="uppercase"
                            mb={3}
                        >
                            {emptyMessage?.Title || (
                                <FormattedMessage
                                    defaultMessage="Your Shopping Bag is Empty"
                                    id="empty_cart.description.shopping_bag_is_empty"
                                />
                            )}
                        </Text>

                        <Text align="center" fontSize="sm" mb={3}>
                            {emptyMessage?.Body || (
                                <FormattedMessage
                                    defaultMessage="Get inspiration for your new wardrobe from the latest looks."
                                    id="empty_cart.message.get_inspiration"
                                />
                            )}
                        </Text>
                        {emptyMessage?.CTA ? (
                            <Button
                                variant={
                                    (() => {
                                        const styling = emptyMessage?.CTA?.Styling
                                        const variantMap = {
                                            'Button': 'solid',
                                            'Text': 'link',
                                            'Outlined': 'outline'
                                        }
                                        return variantMap[styling] || 'solid'
                                    })()
                                }
                                as={Link}
                                href={linkData?.generatedURL}
                                {...(!linkData.stayOnSamePage && {
                                    target: linkData?.target,
                                    rel: linkData?.rel
                                })}
                                width={['100%', '330px']}
                                {...(emptyMessage?.CTA?.BorderColor !== '' || emptyMessage?.CTA?.SearchEmptyMessageBlockBorderWidth !== '') && {
                                    border: `${emptyMessage?.CTA?.SearchEmptyMessageBlockBorderWidth || '1px'} solid`,
                                    borderColor: emptyMessage?.CTA?.BorderColor || 'black'
                                }}
                                {...emptyMessage?.CTA?.CTABackgroundColor !== '' && {
                                    backgroundColor: emptyMessage?.CTA?.CTABackgroundColor
                                }}
                                {...emptyMessage?.CTA?.CTALinkFontWeight !== '' && {
                                    fontWeight: emptyMessage?.CTA?.CTALinkFontWeight
                                }}
                                {...emptyMessage?.CTA?.TextColor !== '' && {
                                    color: emptyMessage?.CTA?.TextColor
                                }}>
                                {emptyMessage.CTA?.CtaLinkText}
                            </Button>
                        ) : (
                            <Button as={Link} href={'/'} width={['100%', '330px']}>
                                <FormattedMessage
                                    defaultMessage="See New Arrivals"
                                    id="empty_cart.link.see_new_arrivals"
                                />
                            </Button>
                        )}
                    </Box>
                </Stack>

                {/* VickyAI Carousels - Bottom Area */}
                {(isOptimizelyLoading || carousels.length > 0) && (
                    <Box minHeight={isOptimizelyLoading ? '300px' : undefined} w="100%">
                        <Skeleton isLoaded={!isOptimizelyLoading} height={isOptimizelyLoading ? '300px' : 'auto'}>
                            {carousels.length > 0 && (
                                <Stack spacing={8} w="100%">
                                    {carousels.map((carousel, index) => (
                                        <Box key={`cart-carousel-${index}`}>
                                            {renderCarouselItems(carousel, `empty-cart-carousel-${index}`, null, isMiniCart)}
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Skeleton>
                    </Box>
                )}
            </Container>
        </Box>
    )
}

EmptyCart.propTypes = {
    isMiniCart: PropTypes.bool
}

export default EmptyCart
